import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { memberApi, friendlyMemberError } from '../api';
import { getLocalConversation, replaceDraftConversation, clearPendingDocuments } from '../localStore';
import { useMemberApp } from '../AppProvider';
import Composer from '../components/Composer';
import Thread from '../components/Thread';
import { memberPath } from '@/config/hosts';

export default function ChatPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, freeRemaining, setFreeRemaining, setUpgradeOpen, setConversations } = useMemberApp();
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState('');
  const [retryText, setRetryText] = useState('');
  const completing = useRef(false);

  useEffect(() => {
    completing.current = false;
  }, [id]);

  useEffect(() => {
    let live = true;
    setMissing(false);
    setError('');
    const cached = getLocalConversation(user, id);
    if (cached?.messages?.length) setMessages(cached.messages);
    else setMessages([]);
    if (String(id || '').startsWith('local_')) return () => { live = false; };
    memberApi.conversation(id)
      .then((data) => {
        if (!live) return;
        setMessages(data.messages || []);
      })
      .catch(() => {
        if (live && !cached?.messages?.length) setMissing(true);
      });
    return () => { live = false; };
  }, [id, user]);

  const applyResult = (result, optimisticId) => {
    const nextId = result.conversation_id || result.conversation?.id;
    if (nextId && nextId !== id && String(id).startsWith('local_')) {
      replaceDraftConversation(user, id, result);
      setConversations((prev) => [result.conversation, ...prev.filter((c) => c.id !== id && c.id !== nextId)].filter(Boolean));
      navigate(memberPath(`/c/${nextId}`), { replace: true });
      return;
    }
    setMessages((prev) => [
      ...prev.filter((m) => m.id !== optimisticId),
      result.user_message,
      result.assistant_message,
    ].filter(Boolean));
    if (result.conversation) {
      setConversations((prev) => [result.conversation, ...prev.filter((c) => c.id !== result.conversation.id)]);
    }
    if (typeof result.free_remaining === 'number') setFreeRemaining(result.free_remaining);
  };

  const send = async (text, { alreadyShown, documentIds, tool, channel } = {}) => {
    if (busy || !text) return;
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }
    const optimistic = alreadyShown
      ? null
      : { id: `tmp-${Date.now()}`, role: 'user', content: text, conversation_id: id };
    if (optimistic) setMessages((prev) => [...prev, optimistic]);
    setBusy(true);
    setError('');
    setRetryText('');
    try {
      const cached = getLocalConversation(user, id);
      const draftDocs = cached?.conversation?.document_ids || [];
      const draftTool = cached?.conversation?.tool;
      const ids = [...(documentIds || []), ...draftDocs].filter(Boolean);
      const body = {
        message: text,
        document_ids: Array.from(new Set(ids)).slice(0, 8),
        language: user?.preferred_language || 'en',
      };
      if (tool || draftTool) body.tool = tool || draftTool;
      if (channel) body.channel = channel;
      if (!String(id).startsWith('local_')) body.conversation_id = id;
      const result = await memberApi.chat(body);
      clearPendingDocuments(user);
      applyResult(result, optimistic?.id);
    } catch (err) {
      if (err.status === 402 && !isPro) setUpgradeOpen(true);
      else {
        setError(err.status === 402 ? 'Vani could not finish that just now. Try again.' : friendlyMemberError(err));
        setRetryText(text);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (completing.current) return;
    const last = messages[messages.length - 1];
    const shouldComplete = location.state?.pending || (last?.role === 'user' && messages.length === 1 && String(id).startsWith('local_'));
    if (!shouldComplete || !last || last.role !== 'user') return;
    completing.current = true;
    send(last.content, { alreadyShown: true });
  }, [id, messages, location.state]);

  if (missing) {
    return (
      <div className="by-page">
        <div className="by-empty">
          <h2>This conversation is not here</h2>
          <p>It may have been archived. Start a new one.</p>
          <button type="button" className="by-btn by-btn-dark" onClick={() => navigate('/app')}>New chat</button>
        </div>
      </div>
    );
  }

  return (
    <div className="by-stage">
      <Thread messages={messages} pending={busy} />
      <div className="by-center">
        {error ? (
          <div className="by-chat-error" role="alert">
            <p>{error}</p>
            {retryText ? (
              <button type="button" className="by-btn by-btn-ghost" onClick={() => send(retryText, { alreadyShown: true })}>
                Try again
              </button>
            ) : null}
          </div>
        ) : null}
        <Composer
          onSend={send}
          disabled={busy}
          placeholder={busy ? 'Vani is writing…' : 'Ask a follow-up'}
          conversationId={String(id || '').startsWith('local_') ? '' : id}
        />
      </div>
    </div>
  );
}
